/**
 * Centralized Theme Configuration
 *
 * This file contains all the design tokens and styling constants
 * used throughout the application for consistent theming.
 */

// Color Palette
export const colors = {
  // Primary Brand Colors
  primary: {
    main: "#1890ff",
    dark: "#004fac",
    light: "#40a9ff",
    lighter: "#69c0ff",
    lightest: "#e6f7ff",
  },

  // Secondary Colors
  secondary: {
    main: "#52c41a",
    dark: "#389e0d",
    light: "#73d13d",
    lighter: "#95de64",
    lightest: "#f6ffed",
  },

  // Neutral Colors
  neutral: {
    white: "#ffffff",
    gray50: "#fafafa",
    gray100: "#f5f5f5",
    gray200: "#f0f0f0",
    gray300: "#d9d9d9",
    gray400: "#bfbfbf",
    gray500: "#8c8c8c",
    gray600: "#595959",
    gray700: "#434343",
    gray800: "#262626",
    gray900: "#1f1f1f",
    black: "#000000",
  },

  // Semantic Colors
  success: "#52c41a",
  warning: "#faad14",
  error: "#ff4d4f",
  info: "#1890ff",

  // Background Colors
  background: {
    primary: "#ffffff",
    secondary: "#fafafa",
    tertiary: "#f5f5f5",
  },

  // Text Colors
  text: {
    primary: "rgba(0, 0, 0, 0.88)",
    secondary: "rgba(0, 0, 0, 0.65)",
    tertiary: "rgba(0, 0, 0, 0.45)",
    inverse: "#ffffff",
  },
};

// Spacing Scale (based on 8px grid)
export const spacing = {
  xs: "4px",
  sm: "8px",
  md: "16px",
  lg: "24px",
  xl: "32px",
  xxl: "48px",
  xxxl: "64px",
};

// Border Radius
export const borderRadius = {
  none: "0px",
  sm: "2px",
  md: "4px",
  lg: "8px",
  xl: "12px",
  xxl: "16px",
  full: "9999px",
};

// Typography
export const typography = {
  fontFamily: {
    base: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
    mono: "'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace",
  },
  fontSize: {
    xs: "12px",
    sm: "14px",
    base: "16px",
    lg: "18px",
    xl: "20px",
    "2xl": "24px",
    "3xl": "30px",
    "4xl": "36px",
    "5xl": "48px",
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// Shadows
export const shadows = {
  none: "none",
  sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  base: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
};

// Transitions
export const transitions = {
  fast: "150ms cubic-bezier(0.4, 0, 0.2, 1)",
  base: "300ms cubic-bezier(0.4, 0, 0.2, 1)",
  slow: "500ms cubic-bezier(0.4, 0, 0.2, 1)",
};

// Breakpoints
export const breakpoints = {
  xs: "480px",
  sm: "576px",
  md: "768px",
  lg: "992px",
  xl: "1200px",
  xxl: "1600px",
};

// Z-Index Scale
export const zIndex = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

// Responsive Icon Sizes (using rem for scalability)
export const iconSizes = {
  xs: "1rem", // 16px at default
  sm: "1.25rem", // 20px
  md: "1.5rem", // 24px
  lg: "2rem", // 32px
  xl: "2.5rem", // 40px
  "2xl": "3rem", // 48px
  "3xl": "3.5rem", // 56px
  "4xl": "4rem", // 64px
};

// Responsive Card Widths
export const cardSizes = {
  minWidth: {
    sm: "200px",
    md: "240px",
    lg: "280px",
    xl: "320px",
  },
  maxWidth: {
    sm: "300px",
    md: "400px",
    lg: "500px",
    xl: "600px",
    full: "100%",
  },
};

// Layout Constants
export const layout = {
  header: {
    height: "72px",
    background: colors.primary.dark,
    color: colors.text.inverse,
    padding: `${spacing.md} ${spacing.xl}`,
    shadow: shadows.md,
  },
  content: {
    maxWidth: "1400px",
    padding: `${spacing.xl} ${spacing.lg}`,
    background: colors.background.secondary,
  },
  sidebar: {
    width: "280px",
    background: colors.neutral.white,
    padding: spacing.lg,
  },
  card: {
    minHeight: "120px",
    padding: spacing.xl,
  },
  button: {
    height: "44px",
    heightLarge: "48px",
    widthLarge: "300px",
  },
};

// Component Variants
export const variants = {
  button: {
    primary: {
      background: colors.primary.main,
      color: colors.text.inverse,
      hover: colors.primary.light,
    },
    secondary: {
      background: colors.neutral.gray100,
      color: colors.text.primary,
      hover: colors.neutral.gray200,
    },
  },
  card: {
    default: {
      background: colors.neutral.white,
      border: colors.neutral.gray300,
      shadow: shadows.base,
      radius: borderRadius.lg,
    },
    elevated: {
      background: colors.neutral.white,
      border: "transparent",
      shadow: shadows.lg,
      radius: borderRadius.xl,
    },
  },
};

// Media Query Utilities
export const mediaQueries = {
  xs: `@media (min-width: ${breakpoints.xs})`,
  sm: `@media (min-width: ${breakpoints.sm})`,
  md: `@media (min-width: ${breakpoints.md})`,
  lg: `@media (min-width: ${breakpoints.lg})`,
  xl: `@media (min-width: ${breakpoints.xl})`,
  xxl: `@media (min-width: ${breakpoints.xxl})`,
};

// Export default theme object
export const theme = {
  colors,
  spacing,
  borderRadius,
  typography,
  shadows,
  transitions,
  breakpoints,
  mediaQueries,
  iconSizes,
  cardSizes,
  zIndex,
  layout,
  variants,
};

export default theme;
